import { errorCodes } from '../../utils/errorCodes';
import { tableTypes } from '../../components/Pages/Reports/Table';
import { cardTypes } from '../../components/Metrics/cardTypes';
import { filterIds, reportGroup, reportViews } from '../../utils/filters';
import {
    AGENT_ACTIVITY_STATUS,
    ANONYMIZATION_TYPES,
    CARD_SECTIONS,
    GROUP_BY,
    PAGING_ENTITIES,
    REPORT_GUIDANCE_TYPES,
    USER_ROLES
} from '../../utils/constants';

import { NOTIFICATION_TYPES, VALIDATION_CODES } from '../../components/Pages/Keywords/constants';
import { AVERAGE_KEY } from '../../utils/keywordsChart';

const callTypes = {
    all: 'Alle Anfragen',
    product: 'Produktanfragen',
    service: 'Serviceanfragen',
    productConversion: 'Produktanfragen Conversion',
    serviceConversion: 'Serviceanfragen Conversion'
};

const overviewWordsCardConfig = () => ({
    labels: {
        say: '„Sagen”-Gebrauch',
        dontSay: '„Nicht Sagen”-Gebrauch',
        [AVERAGE_KEY]: '„Sagen”-Gebrauch',
        keywords: 'Keywords',
        emptyFilter: 'Wählen...'
    }
});

const wordsCardConfig = name => ({
    columns: {
        name,
        say: '„Sagen”-Gebrauch',
        trend: '',
        calls: 'Anrufe'
    }
});

const overviewSpeechRatioCardConfig = () => ({
    labels: {
        speechRatio: 'Sprechanteil',
        speechRate: 'Sprechtempo'
    }
});

const speechRationCardConfig = name => ({
    columns: {
        name,
        speechRatio: {
            left: 'Sprechen',
            right: 'Zuhören'
        },
        speechRate: 'Tempo (W/min)'
    }
});

const gamificationScorePointsConfig = () => ({
    columns: {
        name: 'Agent',
        place: 'Position',
        points: 'Punkte',
        avgPoints: 'Punkte/Anruf'
    }
});

const callDurationStatsConfig = () => ({
    count: 'Anrufe',
    min: 'min',
    avg: 'Durchschnitt:',
    sec: 'sek',
    buttonLabel: 'Anrufe ansehen'
});

const callAmountConfig = () => ({
    total: 'Gesamt: %{count}'
});

const callTopics = {
    new_contract_or_extension: 'Neuvertrag/Verlängerung',
    contract_change: 'Vertragsänderung',
    contract_issue: 'Vertragsanliegen',
    contract_cancellation: 'Vertragskündigung',
    disruption: 'Störung',
    technical_problem: 'Technisches Problem',
    current_order: 'Laufende Bestellung',
    invoice_issue: 'Rechnungsanliegen',
    unknown: 'Unbekannt',
    mobile: 'Mobilfunk',
    internet_landline: 'Internet/Festnetz',
    internet: 'Internet/Festnetz',
    tv: 'TV',
    sim_card: 'SIM-Karte',
    router: 'Router',
    giga_product: 'Giga-Produkte',
    unitymedia: 'UnityMedia',
    appointment: 'Technikertermin',
    move: 'Umzug',
    my_vodafone: 'MeinVodafone'
};

const callTopicsOverviewCardConfig = {
    columns: {
        topic: 'Anrufthema',
        frequency: 'Anrufe',
        amount: 'Anrufe',
        saleRatio: 'Sale',
        conversion: 'Conversion'
    }
};

const agentSalesByCallTopicsConfig = {
    columns: {
        topic: 'Anrufthema',
        sales: 'Sales',
        calls: 'Anrufe',
        agent: 'Agent'
    }
};

function sentimentTeamComparisonConfig() {
    return {
        labels: {
            keywords: 'Teams ausgewählt',
            sentimentComparisonFilter: 'Durchschnitt aller Teams',
            emptyFilter: 'Wählen...'
        },
        columns: {
            teams: 'Teams',
            customerSentiment: 'Stimmung (Kunde)',
            agentSentiment: 'Stimmung (Mitarbeiter)'
        },
        legend: {
            positive: 'positiv Stimmung',
            neutral: 'neutral Stimmung',
            negative: 'negativ Stimmung'
        }
    };
}

function sentimentTrendCustomerConfig() {
    return {
        labels: {
            sentimentTrendFilter: 'Teams',
            average: 'Durchschnitt aller Teams (+)',
            averagePositive: 'Durchschnitt aller Teams (+)',
            averageNegative: 'Durchschnitt aller Teams (-)'
        }
    };
}

export const de = {
    login: {
        title: 'Anmeldung',
        auth_error: {
            general: 'Fehler. Überprüfe deine Eingaben oder versuche es später nochmals.',
            expired:
                'Ihr Passwort ist abgelaufen. Aktualisieren Sie Ihr Passwort über die Funktion "Passwort vergessen" auf dem Anmeldebildschirm.'
        },
        email: {
            label: 'E-Mail'
        },
        password: {
            label: 'Passwort'
        },
        submit: {
            title: 'Anmelden',
            in_progress: 'Verbinde...'
        },
        forgotPassword: 'Passwort vergessen?'
    },
    recovery: {
        title: 'Passwort vergessen?',
        description:
            'Bitte geben Sie den E-Mail-Adresse ein, mit dem Sie sich registriert haben. Wir senden Ihnen eine E-Mail zu, in der die Anleitung zum Zurücksetzen Ihres Passwortes steht.',
        email: {
            label: 'E-Mail'
        },
        password: {
            label: 'Passwort'
        },
        submit: {
            title: 'Passwort zurücksetzen',
            in_progress: 'Verbinde...'
        },
        sent: {
            title: 'E-Mail gesendet',
            description: 'Wir haben Ihnen eine E-Mail mit einem Link zum Zurücksetzen Ihres Passworts gesendet.'
        },
        no_email:
            'Wenn Sie keine E-Mail erhalten haben: Überprüfen Sie, ob sich die E-Mail in Ihrer Spam-E-Mail-Box befindet oder ob der von Ihnen eingegebene Benutzername womöglich falsch ist.',
        back: 'Zurück',
        reset: {
            title: 'Passwort zurücksetzen',
            description: 'Vergeben Sie ein neues Passwort für das Konto.',
            recommended: 'Bitte wählen Sie zu Ihrer eigenen Sicherheit ein Passwort, das Sie noch nie verwendet haben.'
        },
        errors: {
            unknown_error: 'Es ist leider ein unbekannter Fehler aufgetreten.', // Unfortunately, an unknown error has occurred.
            password_too_weak: 'Das Passwort ist zu schwach.',
            password_too_short: 'Das Passwort ist zu kurz (mindestens 12 Zeichen).',
            err_password_repeated:
                'Das Passwort kann nicht wiederverwendet werden. Bitte wählen Sie ein neues Passwort, das kürzlich nicht verwendet wurde.'
        }
    },
    general: {
        privacyInfo: {
            graphs: 'Die zugrundeliegenden Daten erfüllen die Kriterien zur Gewährleistung des Datenschutzes nicht und können daher nicht angezeigt werden.'
        },
        error: {
            unknown: 'Ein unbekannter Fehler ist aufgetreten.',
            statsError:
                'Bitte beachten: Die Statistiken ab dem 1. Februar sind momentan nicht verfügbar. Wir arbeiten an einer Lösung die Daten so schnell wie möglich wiederherzustellen!',
            reportsError:
                'Aufgrund einer technischen Störung ist i2x aktuell nur beschränkt verfügbar. Wir arbeiten daran, dies schnellstmöglich zu beheben.'
        },
        modal: {
            title: {
                hierarchySelection: '[DE]Use the following hierarchy to navigate and select teams.'
            },

            buttons: {
                clear: 'Löschen',
                apply: 'Anwenden',
                select: 'Auswählen',
                cancel: 'Abbrechen',
                save: 'Speichern'
            },

            searchPlaceholder: 'Suchen'
        }
    },
    whatsNew: {
        title: 'Was ist neu?',
        close: 'Schließen',
        new: 'Neu',
        markAllAsRead: 'Alles als gelesen markieren'
    },
    nav: {
        dashboard: 'Dashboard',
        scorecard: 'Scorecard',
        i2x_metrics: 'I2X Kennzahlen',
        overview: 'Overview',
        hcsat: 'CSAT',
        reports: 'Berichte',
        keywordManager: 'Keyword Manager',
        userManagement: 'Teams',
        calls: 'Anrufe',
        today: 'Heute',
        sentimentAnalysis: 'Stimmung',
        sales: 'Sales',
        kpiManager: 'KPI Manager',
        qualityManagement: 'QM',
        qualityAssurance: 'QA',
        callHistory: 'Anrufhistorie'
    },
    sales: {
        filters: {
            button: 'Aktualisieren'
        },
        error: 'Oops! Bitte versuche, die Seite zu aktualisieren, um die Ergebnisse zu sehen.',
        sales_in_all_categories: 'Gesamtverkäufe',
        total_sales_per_hour: 'Gesamtverkäufe pro Stunde',
        total_sales_per_category: 'Gesamtverkäufe pro Kategorie',
        total_sales_per_team: 'Gesamtverkäufe pro Team',
        no_team: 'Kein Team',
        agent_leaderboard: 'Agenten-Rangliste',
        agent: 'Agent',
        team: 'Team',
        location: 'Standort',
        date: 'Datum',
        product_board: 'Produkt-Rangliste',
        category: 'Kategorie',
        product: 'Produkt',
        sales: 'Sales',
        time: 'Zeit',
        position: 'Pos.',
        last_update: 'Letztes Update'
    },
    kpiManager: {
        errors: {
            SomeError: 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.',
            UnknownError: 'Ein unbekannter Fehler ist aufgetreten.'
        },
        table: {
            name: 'Name',
            createdAt: 'Erstellt am',
            teams: 'Teams',
            noData: 'Keine Daten verfügbar',
            archived: 'Archiviert',
            edit: 'Bearbeiten',
            close: 'Schließen'
        },
        project: {
            add: 'Projekt hinzufügen',
            provideValue: 'Bitte einen Wert eingeben',
            name: 'Projektname',
            archive: 'Projekt archivieren',
            products: 'Produkte',
            product: 'Produkt',
            teams: 'Teams',
            category: 'Kategorie',
            save: 'Speichern',
            close: 'Schließen',
            closeUnsavedAlert:
                'Sind Sie sicher, dass Sie dies schließen möchten, ohne zu speichern? Nicht gespeicherte Änderungen gehen verloren.',
            new: 'Neu',
            teamName: 'Team',
            locationName: 'Standorte'
        },
        displayArchive: 'Archiv anzeigen',
        saveSuccessful: 'Speichern erfolgreich'
    },
    qualityManagement: {
        addEvaluation: 'Bewertung hinzufügen',
        noEvaluations: 'Dieser Anruf hat noch keine Bewertungen',
        dateRange: 'Zeitraum',
        backToList: 'Zurück',
        export: 'Export',
        exporting: 'Exportiere...',
        teamsFilter: 'Teams',
        autoQaWentWrong: 'Beim Erstellen der Auswertung ist ein Fehler aufgetreten.',
        changesSaved: 'Änderungen erfolgreich gespeichert!',
        exportFailed: 'Export fehlgeschlagen. Bitte versuchen Sie es erneut.',
        exportLimitWaringTitle: 'Exportlimit erreicht',
        exportLimitWaringText:
            'Die Anzahl der Bewertungen überschreitet das Limit von 10000. Bitte passen Sie die Filter an, um die Anzahl der Bewertungen zu reduzieren.',
        table: {
            id: 'ID',
            template: 'Vorlage',
            project: 'Projekt',
            callId: 'Anruf-ID',
            callDateTime: 'Anrufdatum',
            status: 'Status',
            evaluatedUser: 'Agent',
            coachComment: 'Coach-Kommentar',
            createdAt: 'Erstellt am',
            createdBy: 'Erstellt von',
            updatedAt: 'Aktualisiert am',
            updatedBy: 'Aktualisiert von',
            createdByAI: 'Automatische Bewertung',
            createdByNoneAI: 'Ohne automatische Bewertung',
            score: 'Durchschnittliche Bewertung'
        },
        form: {
            general: 'Allgemein',
            information: 'Information',
            coachingSheet: 'Fragebogen',
            agent: 'Agent',
            callId: 'Anruf-ID',
            callDate: 'Anrufdatum',
            comment: 'Coach-Kommentar'
        },
        view: {
            title: 'Bewertung Details',
            edit: 'Bearbeiten',
            close: 'Schließen',
            deleteTitle: 'Bitte bestätigen',
            deleteText: 'Bist du sicher, dass du das löschen möchtest?',
            cancel: 'Abbrechen',
            delete: 'Löschen'
        },
        templates: 'Vorlagen',
        reports: 'Berichte',
        evaluations: 'Bewertungen',
        filters: 'Filter',
        reset: 'Zurücksetzen',
        saveEvaluation: 'Bewertung speichern',
        templateNotFound: 'Vorlage wird nicht gefunden',
        templateTable: {
            id: 'ID',
            title: 'Vorlage',
            questionnaire: 'Fragen',
            llmPrologue: 'LLM-Prolog',
            callsNotEvaluated: 'Anrufe ohne Bewertung',
            callsEvaluated: 'Bewertete Anrufe'
        },
        editTemplate: {
            title: 'Vorlagendetails',
            close: 'Schließen',
            name: 'Vorlagenname',
            template: 'Vorlage',
            llmPrologue: 'LLM-Prolog',
            llmProviderName: 'LLM-Anbietername',
            coachingSheet: 'Fragebogen',
            automationRules: 'Automatisierte Aufgaben',
            addTeamTask: 'Teamaufgabe hinzufügen',
            team: 'Team',
            validFrom: 'Gültig ab',
            validTo: 'Gültig bis',
            minDurationSec: 'Mindestgesprächsdauer',
            minCharsLen: 'Minimale Textlänge',
            noTasks: 'Keine Aufgaben',
            sec: 'Sek',
            status: 'Status',
            editAssignment: 'Aufgabe bearbeiten',
            addAssignment: 'Aufgabe hinzufügen'
        },
        stats: {
            averageScore: 'Durchschnittswert',
            totalEvaluations: 'Gesamtbewertungen'
        },
        aggregateBy: 'Aggregieren nach',
        aggregate: {
            byDay: 'Tag',
            byWeek: 'Woche',
            byMount: 'Berg'
        },
        teamTaskStatus: {
            not_started: 'Nicht gestartet',
            completed: 'Abgeschlossen',
            in_progress: 'In Bearbeitung',
            error: 'Fehler'
        }
    },
    dashboard: {
        paging: '%{range} von %{count} %{entity} werden angezeigt',
        pagingEntityNames: {
            [PAGING_ENTITIES.agents]: 'Agenten',
            [PAGING_ENTITIES.agencies]: 'Agenturen',
            [PAGING_ENTITIES.locations]: 'Standorten',
            [PAGING_ENTITIES.objects]: 'Objecte',
            [PAGING_ENTITIES.team]: 'Teams'
        },
        groupByEntityNames: {
            [GROUP_BY.ALL]: 'Alle',
            [GROUP_BY.AGENCY]: 'Agentur',
            [GROUP_BY.TEAM]: 'Team',
            [GROUP_BY.LOCATION]: 'Standort',
            [GROUP_BY.AGENT]: 'Agent'
        },
        callHistory: 'ANRUFE ANSEHEN',
        report: 'BERICHT ANSEHEN',
        fullScreen: 'FULL SCREEN',
        datepicker: {
            select: 'Auswählen',
            cancel: 'Abbrechen',
            presetHeader: 'Voreinstellungen',
            presetOptions: {
                thisWeek: 'Diese Woche',
                last7Days: 'Letze 7 Tage',
                last28Days: 'Letzte 28 Tage',
                lastMonth: 'Letzten Monat',
                last90Days: 'Letzte 90 Tage'
            }
        },
        popups: {
            keywordsUsage: {
                header: '„Sagen“-Gebrauch',
                messageSaid:
                    '„Sagen”-Gebrauch = Wieviel Prozent der Keyword-Gruppen im Durchschnitt pro Anruf angesprochen wurden.',
                messageGroups:
                    'Keyword-Gruppen Gebrauch  = In welchem Prozentsatz der Anrufe diese Gruppe (min. ein Keyword) angesprochen wurde.',
                messageUsed:
                    '„Keyword” Gebrauch = In welchem Prozentsatz der Anrufe ein einzelnes Keyword verwendet wurde.'
            },
            speechRatio: {
                header: 'Gesprächsdaten',
                message: 'Sprechen/Zuhören = Durchschnittlicher Sprechanteil der Agenten (in blau)',
                messageTempo: 'Tempo = Sprechtempo der Agenten in Wörtern pro Minute.'
            },
            privacyPopup: {
                message:
                    'Die zugrundeliegenden Daten erfüllen die Kriterien zur Gewährleistung des Datenschutzes nicht und können daher nicht angezeigt werden.'
            },
            privacyContentBox: {
                message:
                    'Auf Grund der vorhandenen Kriterien zur Gewährleistug des Datenschutzes, ist es möglich, dass nicht alle Daten vollständig angezeigt werden.'
            },
            dataRoomContentBox: {
                message:
                    'Durch den aktivierten Datenraum-Filter werden nur die Daten für aufgezeichnete Anrufe angezeigt, was zu einer Reduzierung der Gesamtdatenmenge führt.'
            }
        },
        header: {
            dateRange: 'Zeitraum',
            participator: 'Teilnehmer'
        },
        cards: {
            errors: {
                header: 'Entschuldigung, etwas ist fehlgeschlagen',
                message: 'Wir wissen schon darüber Bescheid und arbeiten an einer Lösung',
                privacyError:
                    'Die zugrundeliegenden Daten erfüllen die Kriterien zur Gewährleistung des Datenschutzes nicht und können daher nicht angezeigt werden.'
            },
            general: {
                ratedCallsCount: 'Bewertete Anrufe',
                identifiedCallsCount: 'Identifizierte Anrufe',
                createTeam: 'Team erstellen',
                editTeam: 'Team bearbeiten',
                myTeam: 'Alle',
                myLocation: 'Alle Teams',
                noData: 'Im ausgewählten Zeitraum sind keine Daten verfügbar'
            },
            tooltips: {
                productQualityIndex: {
                    header: 'Productanfrage',
                    dialog: 'Gesprächsführungskompetenz',
                    social: 'Sozial und Sprachkompetenz',
                    script: 'Fach und Prozesskompetenz'
                },
                serviceQualityIndex: {
                    header: 'Serviceanfrage',
                    interview: 'Gesprächsführung',
                    speech: 'Ansprache',
                    finish: 'Abschluss'
                },
                callsWithPotential: {
                    title: 'Anrufe mit Potenzial'
                }
            },
            sections: {
                [CARD_SECTIONS.overview]: 'Übersicht',
                [CARD_SECTIONS.agencies]: 'Agenturen',
                [CARD_SECTIONS.location]: 'Standorte',
                [CARD_SECTIONS.agent]: 'Agenten',
                [CARD_SECTIONS.team]: 'Team',
                [CARD_SECTIONS.teams]: 'Teams',
                [CARD_SECTIONS.agency]: 'Agentur',
                [CARD_SECTIONS.sentiment]: ''
            },
            headers: {
                [cardTypes.overviewWords]: '%{companyName} „Sagen“-Gebrauch',
                [cardTypes.overviewAgencyWords]: '%{agencyName} „Sagen“-Gebrauch',
                [cardTypes.overviewAgentWords]: '„Sagen“-Gebrauch',
                [cardTypes.agenciesWords]: 'Agenturen „Sagen“-Gebrauch',
                [cardTypes.agencyAgentsWords]: 'Agenten „Sagen“-Gebrauch',
                [cardTypes.locationWords]: 'Standorte „Sagen“-Gebrauch',
                [cardTypes.locationAgentsWords]: 'Agenten „Sagen“-Gebrauch',
                [cardTypes.overviewLocationTeamsWords]: 'Team „Sagen“-Gebrauch',
                [cardTypes.agencyLocationsWords]: '%{agencyName} „Sagen“-Gebrauch',

                [cardTypes.overviewSpeechRatio]: 'Gesprächsdaten',
                [cardTypes.overviewAgencySpeechRatio]: '%{agencyName} Gesprächsdaten',
                [cardTypes.overviewAgentSpeechRatio]: 'Gesprächsdaten',
                [cardTypes.agenciesSpeechRatio]: 'Agenturen Gesprächsdaten',
                [cardTypes.agencyAgentsSpeechRatio]: 'Agenten Gesprächsdaten',
                [cardTypes.locationSpeechRatio]: 'Standorte Gesprächsdaten',
                [cardTypes.locationTeamsSpeechRatio]: 'Team Gesprächsdaten',
                [cardTypes.locationAgentsSpeechRatio]: 'Agenten Gesprächsdaten',
                [cardTypes.agencyLocationsSpeechRatio]: '%{agencyName} Gesprächsdaten',

                [cardTypes.overviewCallDurationStats]: '%{companyName} Anrufdauer',
                [cardTypes.overviewAgencyCallDurationStats]: '%{agencyName} Anrufdauer',
                [cardTypes.overviewAgentCallDurationStats]: 'Anrufdauer',
                [cardTypes.agencyCallDurationStats]: 'Anrufdauer Agentur',
                [cardTypes.locationCallDurationStats]: 'Anrufdauer Standort',
                [cardTypes.locationTeamsCallDurationStats]: 'Anrufdauer Team',

                [cardTypes.overviewCallAmount]: '%{companyName} Anzahl der Anrufe',
                [cardTypes.overviewAgencyCallAmount]: '%{agencyName} Anzahl der Anrufe',
                [cardTypes.overviewAgentCallAmount]: 'Anzahl der Anrufe',
                [cardTypes.agenciesCallAmount]: 'Anzahl der Anrufe Agenturen',
                [cardTypes.locationCallAmount]: 'Anzahl der Anrufe Standorte',
                [cardTypes.overviewLocationTeamsCallAmount]: 'Anzahl der Anrufe Team',
                [cardTypes.agencyLocationsCallAmount]: '%{agencyName} Anzahl der Anrufe',

                [cardTypes.gamificationScorePoints]: 'i2x Punkte',
                [cardTypes.liveAgents]: 'Live-Agents',
                [cardTypes.sentimentTeamComparison]: 'Teamvergleich',
                [cardTypes.sentimentTrendAgent]: 'Verlauf (Stimmung des Mitarbeiters)',
                [cardTypes.sentimentTrendCustomer]: 'Verlauf (Stimmung des Kunden)'
            },

            [cardTypes.overviewWords]: overviewWordsCardConfig(),
            [cardTypes.overviewAgencyWords]: overviewWordsCardConfig(),
            [cardTypes.overviewAgentWords]: overviewWordsCardConfig(),
            [cardTypes.overviewLocationTeamsWords]: overviewWordsCardConfig(),
            [cardTypes.agenciesWords]: wordsCardConfig('Agentur'),
            [cardTypes.agencyAgentsWords]: wordsCardConfig('Agent'),
            [cardTypes.locationWords]: wordsCardConfig('Standort'),
            [cardTypes.locationAgentsWords]: wordsCardConfig('Agent'),
            [cardTypes.agencyLocationsWords]: wordsCardConfig('Standort'),

            [cardTypes.overviewSpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.overviewAgencySpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.overviewAgentSpeechRatio]: overviewSpeechRatioCardConfig(),
            [cardTypes.agenciesSpeechRatio]: speechRationCardConfig('Agentur'),
            [cardTypes.agencyAgentsSpeechRatio]: speechRationCardConfig('Agent'),
            [cardTypes.locationSpeechRatio]: speechRationCardConfig('Standort'),
            [cardTypes.locationAgentsSpeechRatio]: speechRationCardConfig('Agent'),
            [cardTypes.locationTeamsSpeechRatio]: speechRationCardConfig('Team'),
            [cardTypes.agencyLocationsSpeechRatio]: speechRationCardConfig('Standort'),

            [cardTypes.overviewCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.overviewAgencyCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.overviewAgentCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.agencyCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.locationCallDurationStats]: callDurationStatsConfig(),
            [cardTypes.locationTeamsCallDurationStats]: callDurationStatsConfig(),

            [cardTypes.overviewCallAmount]: callAmountConfig(),
            [cardTypes.overviewAgencyCallAmount]: callAmountConfig(),
            [cardTypes.overviewAgentCallAmount]: callAmountConfig(),
            [cardTypes.agenciesCallAmount]: callAmountConfig(),
            [cardTypes.locationCallAmount]: callAmountConfig(),
            [cardTypes.overviewLocationTeamsCallAmount]: callAmountConfig(),
            [cardTypes.agencyLocationsCallAmount]: callAmountConfig(),

            [cardTypes.gamificationScorePoints]: gamificationScorePointsConfig(),
            [cardTypes.liveAgents]: {
                columns: {
                    name: 'Agent',
                    timestamp: 'Letzte Aktualisierung',
                    status: 'Status'
                },
                status: {
                    unknown: 'Unbekannt',
                    inactive: 'Inaktiv',
                    'logged-in': 'Angemeldet',
                    'in-call': 'Im Gespräch'
                }
            },

            [cardTypes.sentimentTeamComparison]: sentimentTeamComparisonConfig(),
            [cardTypes.sentimentTrendAgent]: sentimentTrendCustomerConfig(),
            [cardTypes.sentimentTrendCustomer]: sentimentTrendCustomerConfig()
        },
        callsDashboard: {
            removeCardTitle: 'Karte entfernen',
            addNewCard: 'Neue Karte hinzufügen',
            addNewCardNote:
                'Um ein neues Preset zu erstellen, gehen Sie zu „Anrufe“, wählen Sie die gewünschten Filter aus und speichern Sie diese als neue Voreinstellung.',
            selectFilterTitle: 'Filter auswählen',
            removeModal: {
                title: 'Karte entfernen?',
                content: 'Möchtest du diese Karte wirklich entfernen?',
                cancel: 'Abbrechen',
                confirm: 'Entfernen'
            },
            moveCardUp: 'Karte nach oben verschieben',
            moveCardDown: 'Karte nach unten verschieben'
        },
        liveDashboard: {
            rank: 'Rang',
            agent: 'Agent',
            points: 'i2x Punkte Rate',
            pointsDescription: 'Erfasste Punkte von möglichen Punkten',
            dontSayRate: 'Nicht Sagen',
            calls: 'Anrufe',
            conversionRate: 'Konversionsrate',
            conversionRateDescription: 'Punkte pro Anruf (Team-Vergleich)',
            status: 'Status',
            statusGreen: 'Im Ziel',
            statusYellow: 'Instabil',
            statusRed: 'Kritisch',
            avgPerAgent: 'Ø pro Agent',
            perAgent: 'Pro Agent',
            totalCalls: 'Anrufe gesamt',
            last2h: 'Letzte 2 Stunden',
            today: 'Heute'
        }
    },
    notifications: {
        error: {
            general: 'Oops... Es ist ein Fehler aufgetreten.',
            [errorCodes.REQUEST_ERROR]: 'Ooops! Bitte Seite neu laden.'
        }
    },
    reports: {
        filters: {
            time: {
                textField: {
                    label: 'Zeitraum',
                    placeholder: 'Auswählen...'
                }
            },
            report: {
                textField: {
                    label: 'Bericht',
                    placeholder: 'Auswählen...'
                },
                selectValues: {
                    serviceScorecard: 'Serviceanfragen',
                    productScorecard: 'Produktanfragen',
                    keywordUse: 'Keyword Gebrauch',
                    keywordGuidance: 'Keyword Gebrauch',
                    keywordMoments: 'Anrufmomente'
                }
            },
            groups: {
                [reportGroup.all]: {
                    singular: '%{companyName} Gesamt',
                    plural: '%{companyName} Gesamt'
                },
                [reportGroup.agencies]: {
                    singular: 'Agentur',
                    plural: 'Agenturen'
                },
                [reportGroup.locations]: {
                    singular: 'Standort',
                    plural: 'Standorte'
                },
                [reportGroup.team]: {
                    singular: 'Team',
                    plural: 'Teams'
                },
                [reportGroup.agents]: {
                    singular: 'Agent',
                    plural: 'Agenten'
                }
            },
            view: {
                textField: {
                    label: 'Ansicht',
                    placeholder: 'Auswählen...'
                },
                searchField: {
                    [reportViews.agents]: {
                        label: 'Deine Auswahl',
                        placeholder: 'Agenten durchsuchen...'
                    }
                },
                selectValues: {
                    overview: '%{companyName} Gesamt',
                    team: 'Teamvergleich',
                    oneTeam: 'Mein Team',
                    agencies: 'Agenturen...',
                    location: 'Alle',
                    locations: 'Standorte...',
                    agents: 'Bestimmte Agenten...',
                    agent: 'Meine Statistiken',
                    agencyOverview: '%{agencyName} Gesamt',
                    teams: 'Teams auswählen...'
                },
                modal: {
                    title: {
                        [reportViews.all]: 'Alle',
                        [reportViews.team]: 'Mein Team',
                        [reportViews.agencies]: 'Wähle eine oder mehrere Agenturen',
                        [reportViews.locations]: 'Wähle einen oder mehrere Standorte',
                        [reportViews.agents]: 'Wähle einen oder mehrere Agenten',
                        [reportViews.teams]: 'Wähle ein oder mehrere Teams'
                    },
                    subtext: {
                        [reportViews.all]: 'Kein Filter angewendet',
                        [reportViews.team]: 'Nur ein Team',
                        [reportViews.agencies]:
                            'Wird mehr als eine Agentur ausgewählt, so wird ein Vergleichsbericht dieser Agenturen erstellt',
                        [reportViews.locations]:
                            'Wird mehr als ein Standort ausgewählt, so wird ein Vergleichsbericht dieser Standorte erstellt',
                        [reportViews.agents]:
                            'Wird mehr als ein Agent ausgewählt, so wird ein Vergleichsbericht dieser Agenten erstellt',
                        [reportViews.teams]:
                            'Wird mehr als ein Team ausgewählt, so wird ein Vergleichsbericht dieser Teams erstellt'
                    },
                    buttons: {
                        clear: 'Löschen',
                        apply: 'Anwenden'
                    },
                    selectAll: {
                        [reportViews.agencies]: 'Alle Agenturen',
                        [reportViews.locations]: 'Alle Standorte',
                        [reportViews.agents]: 'Alle Agenten',
                        [reportViews.teams]: 'Alle Teams'
                    },
                    selectAllGroup: {
                        [reportViews.locations]: 'Alle %{groupName} Standorte',
                        [reportViews.teams]: 'Alle %{groupName} Teams',
                        [reportViews.agents]: 'Alle %{groupName} Agenten'
                    },
                    empty: 'Keine Optionen verfügbar',
                    searchPlaceholder: 'Namen durchsuchen...',
                    noResults: 'Keine Ergebnisse gefunden'
                },
                choiceLabel: {
                    one: 'Agenturen - %{name}',
                    more: '%{number} Agenturen'
                },
                label: {
                    [reportViews.locations]: {
                        one: 'Standorte - %{name}',
                        more: '%{number} Standorte'
                    },
                    [reportViews.agencies]: {
                        one: 'Agenturen - %{name}',
                        more: '%{number} Agenturen'
                    },
                    [reportViews.agents]: {
                        one: 'Agenten - %{name}',
                        more: '%{number} Agenten'
                    },
                    [reportViews.teams]: {
                        one: 'Teams - %{name}',
                        more: '%{number} Teams'
                    }
                }
            },
            member: {
                textField: {
                    label: 'Teilnehmer',
                    placeholder: 'Auswählen...'
                },
                selectValues: {
                    all: 'Alle Agenten (%{number})',
                    agents: 'Agenten (%{number})',
                    trainees: 'Trainees (%{number})'
                }
            },
            button: 'bericht erstellen',
            dataRoomToggleOn: 'Anrufe mit Aufzeichnung',
            dataRoomToggleOff: 'Alle Anrufe'
        },
        tables: {
            [tableTypes.KEYWORD_GUIDANCE]: {
                label: {
                    one: 'Keyword Gebrauch: %{selection}',
                    many: 'Keyword Gebrauch Vergleich: %{selection}'
                },
                cells: {
                    ratedCalls: 'Bewertete Anrufe',
                    whitelist: '„Sagen“-Gebrauch',
                    blacklist: '„Nicht Sagen“-Gebrauch',
                    wordgroup: '(Gruppe)',
                    stats: {
                        ratedCalls: 'Bewertete Anrufe',
                        [REPORT_GUIDANCE_TYPES.whitelists]: {
                            overallStats: '„Sagen“-Gebrauch'
                        },
                        [REPORT_GUIDANCE_TYPES.blacklists]: {
                            overallStats: '„Nicht Sagen“-Gebrauch'
                        },
                        wordgroup: '(Gruppe)'
                    }
                }
            },
            scorecard: {
                cells: {
                    samples: 'Bewertete Anrufe',
                    overview: 'Übersicht',
                    weight: 'Gewichtung'
                },
                columns: {
                    weight: 'Gewichtung'
                }
            },
            emptyState: {
                title: 'Erstelle detaillierte Berichte',
                subtitle: 'Verwende die Dropdowns Liste um deinen gewünschten Bericht zu erstellen'
            },
            noDataState: {
                title: 'Im ausgewählten Zeitraum sind keine Daten verfügbar',
                subtitle: ''
            },
            privacy: {
                cell: 'verborgen'
            },
            sumColumns: {
                title: 'Gesamt'
            }
        }
    },
    filters: {
        defaultPlaceholder: 'Auswählen',
        agents: {
            all: 'Alle Agenten',
            agent: 'Agenten',
            trainee: 'Trainees',
            label: 'Teilnehmer',
            placeholder: 'Auswählen...'
        },
        topic: {
            all: 'Alle Themen',
            frequent: 'Top Themen',
            select: 'Anrufthemen auswählen...',
            textField: {
                label: 'Anrufthemen',
                placeholder: 'Auswählen...'
            }
        },
        callType: {
            ...callTypes,
            label: 'Anruftyp',
            placeholder: 'Auswählen...'
        },
        teamId: {
            all: 'Alle Teams',
            label: 'Team',
            textField: {
                label: 'Team',
                placeholder: 'Auswählen...'
            }
        },
        conversion: {
            all: 'Conversion',
            take: 'Anrufe mit Conversions',
            noTake: 'Anrufe ohne Conversions',
            label: 'Conversion',
            placeholder: 'Auswählen...'
        },
        reportType: {
            label: 'Bericht',
            placeholder: 'Auswählen...'
        },
        speakerType: {
            label: 'Sprecher',
            placeholder: 'Auswählen...'
        },
        granularity: {
            label: 'Granularity',
            day: 'Täglich',
            week: 'Wöchentlich',
            month: 'Monatlich',
            quarter: 'Vierteljährlich',
            year: 'Jährlich'
        },
        agencies: {
            all: 'Alle Agenturen'
        },
        locations: {
            all: 'Alle Standorte'
        },
        locationAndAgency: {
            all: 'Vodafone'
        },
        hierarchySelection: {
            agency: '[DE]Agencies...',
            location: '[DE]Locations...',
            team: '[DE]Teams...',
            agent: '[DE]Certain Agents...',
            selected: {
                agency: {
                    plural: '%{selection} [DE] Agencies',
                    singular: '%{selection} [DE] Agency'
                },
                location: {
                    plural: '%{selection} [DE] Locations',
                    singular: '%{selection} [DE] Location'
                },
                team: {
                    plural: '%{selection} [DE] Teams',
                    singular: '%{selection} [DE] Team'
                },
                agent: {
                    plural: '%{selection} [DE] Agents',
                    singular: '%{selection} [DE] Agent'
                }
            }
        }
    },
    common: {
        sentiment: 'Stimmung',
        sentimentAnalysis: {
            teams: 'Teams',
            agentSentiment: 'Agenten Sentiment',
            customerSentiment: 'Kunden Sentiment'
        },
        sentimentNoCalls: 'Keine Anrufe mit Sentiment',
        allTeams: 'Alle Teams',
        beta: 'BETA',
        quarter: 'Quartal',
        calls: 'Anrufe',
        appInitializationFailed: {
            title: 'Die Anwendung kann nicht gestartet werden',
            // prettier-ignore
            subTitle: 'Bitte versuche es später erneut. Wir sind schon informiert und arbeiten an einer Lösung.',
            button: 'Erneut versuchen'
        },
        failedState: {
            title: 'Etwas ist schief gelaufen',
            subtitle: 'Bitte versuche es später erneut'
        },
        agentActivityStatus: {
            [AGENT_ACTIVITY_STATUS.ANY]: 'Beliebig',
            [AGENT_ACTIVITY_STATUS.ACTIVE]: 'Aktiv',
            [AGENT_ACTIVITY_STATUS.INACTIVE]: 'Inaktiv'
        },
        roles: {
            [USER_ROLES.CORE_AGENT]: 'Agent',
            [USER_ROLES.CORE_TEAM_LEAD]: 'Teamleiter',
            [USER_ROLES.LOCATION]: 'Standortmanager',
            [USER_ROLES.AGENCY]: 'Agenturmanager',
            [USER_ROLES.COMPANY]: 'Companymanager',
            [USER_ROLES.COMPANY_EXTERNAL]: 'Companymanager [+]',
            i2x_admin: 'Supervisor',
            teamLead: 'Teamleiter'
        },
        discardDialog: {
            title: 'Änderungen verwerfen?',
            content: `Ihre Änderungen werden nicht gespeichert.`,
            confirm: 'Verwerfen',
            cancel: 'Abbrechen'
        },
        emptyTeamsMessage: 'Sie haben kein Team',
        save: 'Speichern',
        cancel: 'Abbrechen',
        confirm: 'Bestätigen'
    },
    languages: {
        en: 'Englisch',
        de: 'Deutsch'
    },
    keywordManager: {
        subtitle: 'Verwaltung und Bearbeitung von Keywords',
        teams: 'Teams',
        visibilityLabels: {
            agent: 'Ansprache',
            statistics: 'Anrufmomente'
        },
        setConfig: {
            speakerLabel: 'Sprecher:',
            mention: 'ansprechen',
            targets: {
                agent: 'Agenten',
                client: 'Kunde',
                both: 'Beide'
            },
            typeLabels: {
                whitelist: 'sollten',
                blacklist: 'sollten nicht'
            }
        },
        importKeywords: {
            title: 'Keywords Importieren',
            subtitle: 'Sie können Keywords aus verschiedenen Teams hinzufügen',
            assignTitle: ' ZUWEISEN...',
            yourSelection: 'Deine Auswahl',
            cancelButton: 'ABBRECHEN',
            confirmButton: 'IMPORTIEREN',
            from: 'von',
            doSaySection: 'Sagen',
            dontSaySection: 'Nicht Sagen',
            imported: 'Keywords erfolgreich importiert!',
            validation: {
                similarKeywords: 'Ähnliche Keywords',
                similarKeywordsMessage: 'Ähnliche Keywords gibt es bereits!',
                similarKeywordsDescription:
                    'Es gibt ähnliche Keywords, die bereits in der aktuellen Liste vorhanden sind. Daher werden sie nicht in das Team importiert.'
            }
        },
        importTemplate: {
            templates: 'Vorlage',
            title: 'Team zuweisen...',
            subtitle: 'Sie können diese Keywords mehreren Teams zuweisen.',
            cancelButton: 'ABBRECHEN',
            confirmButton: 'ZUWEISEN',
            info: 'In der Hauptvorlage können Sie eine Sammlung von Keywords erstellen oder Keywords aus einem bereits bestehenden team importieren. Die Keywords aus der Hauptvorlage können dann einem oder mehreren teams zugewiesen werden. Änderungen an der Hauptvorlage wirken sich NICHT automatisch auf Teams aus, die diese Keywords gemeinsam nutzen.',
            overwriteWarning:
                'Wenn Sie diese Option auswählen, werden alle vorhandenen Keywords für die ausgewählten Teams gelöscht. Lassen Sie diese Option offen, wenn Sie die Keywords in dieser Vorlage zu den bereits.',
            replaceAllKeywords: 'Ersetze alle Keywords für die ausgewählten Teams durch diese Vorlage.',
            overwriteModal: {
                title: 'Sind Sie sich sicher?',
                content:
                    'Mit dieser Option werden alle vorhandenen Keywords aus dem/den ausgewählten Team/s gelöscht und durch die Keywords aus dieser Vorlage ersetzt. Diese Aktion kann nicht rückgängig gemacht werden!',
                confirm: 'ALLE KEYWORDS LÖSCHEN UND ERSETZEN',
                cancel: 'ABBRECHEN'
            }
        },
        phraseDetection: {
            tabTitle: 'Phrase Detection Keywords'
        },
        newSet: 'Neues Set',
        importKeyword: 'Importieren',
        deleteSet: 'Set löschen',
        language: 'Sprache',
        tabs: {
            whitelist: `Sagen`,
            blacklist: `Nicht Sagen`,
            phraseDetection: 'Schlüsselwörter für Anrufereignisse'
        },
        header: {
            groupName: {
                text: 'Gruppenname',
                helpText: 'Beschreibungstext für die Wortgruppe. (Kein Schlüsselwort).'
            },
            keyword: {
                text: 'Keyword',
                helpText: ''
            },
            relatedTerms: {
                text: 'Verwandte Begriffe',
                helpText:
                    'Sie können verwandte Schlüsselwörter oder längere Phrasen verwenden, die etwas auf andere Art bzw. synonym ausdrücken.'
            },
            matchingLogic: {
                text: 'Erkennungslogik',
                helpText1:
                    'Sie können zwischen verschiedenen Erkennungslogiken für Schlüsselwörter bzw. Phrasengruppe wählen.',
                helpText2:
                    '%{strong}- Strikt:%{_strong} Diese Option verlangt vom Agenten, das genaue Schlüsselwort oder die genaue Phrase zu sagen.',
                helpText3:
                    '%{strong}- Flexibel:%{_strong} Diese Option gibt dem Agenten, die Möglichkeit, sich anders auszudrücken, solange der Kontext derselbe ist. Diese Option schließt verschiedene Konjugationen und Deklinationen von Wörtern mit ein.',
                helpText4:
                    'Bei Conditional Keywords (bedingte Schlüsselwörter) ist nur die strikte Erkennungslogik verfügbar.'
            }
        },
        content: {
            keywordGroup: 'Keyword Gruppe',
            singleKeyword: 'Einzelnes Keyword',
            placeholders: {
                addTopic: 'Gruppenname eingeben...',
                keywordSetName: 'Keyword Set Name',
                addAlternative: 'Keyword hinzufügen...',
                emptySetError: 'Bitte gib einen Keyword Set Namen ein'
            },
            matchingLogic: {
                exactMatch: 'Strikt',
                rephrasingAllowed: 'Flexibel',
                disabledMessage: 'Für Conditional Keywords ist nur die strike Erkennungslogik verfügbar.'
            }
        },
        validationMessages: {
            [VALIDATION_CODES.DUPLICATED_IN_WHITELIST]: 'Dieses Keyword ist bereits in Ihrer „Sagen“ Liste.',
            [VALIDATION_CODES.DUPLICATED_IN_BLACKLIST]: 'Dieses Keyword ist bereits in Ihrer „Nicht Sagen“ Liste.',
            [VALIDATION_CODES.UNSUPPORTED_CHARS]: 'Keywords dürfen nur aus Buchstaben und Zahlen bestehen',
            [VALIDATION_CODES.UNSUPPORTED_NUM]: 'Zahlen von eins bis zehn müssen ausgeschrieben werden',
            [VALIDATION_CODES.TOO_LONG]: 'Dieses Keyword ist zu lang',
            [VALIDATION_CODES.GROUP_NAME_NOT_UNIQUE]: `Dieser Gruppenname ist bereits in Ihrer Liste`,
            [VALIDATION_CODES.KEYWORD_NOT_UNIQUE]: `Dieses Keyword ist bereits in Ihrer Liste`,
            [VALIDATION_CODES.KEYWORD_COMBINATION_NOT_UNIQUE]: `Dieses Keyword-Kombination ist bereits in Ihrer Liste`,
            [VALIDATION_CODES.EMPTY_GROUP_NAME]: `Bitte geben Sie einen Gruppennamen ein`,
            default: 'Dieses Keyword ist nicht gültig'
        },
        superWordMessage: `Das Superwort ist mit einem Stern markiert und ist %{superWordPoints} Punkte wert`,
        actions: {
            save: 'Speichern',
            undo: 'Änderungen verwerfen',
            undoLastChange: 'Letzte Änderung verwerfen'
        },
        saveModal: {
            title: 'Änderungen verwerfen?',
            content: 'Du verlässt den Keyword-Manager, ohne deine Änderungen zu speichern.',
            cancel: 'Abbrechen',
            confirm: 'OK'
        },
        confirmationMessage: {
            changesSaved: 'Änderungen erfolgreich gespeichert!',
            notifyAgents1: 'Agenten werden über diese Änderungen nicht benachrichtigt.',
            notifyAgents2: 'Bitte informieren Sie Ihre Agenten, falls notwendig.'
        },
        deleteConfirm: {
            title: 'Keyword Set löschen',
            content: 'Dieses Keyword Set und die dazugehörigen Keywords werden entfernt.'
        },
        keywordsLimitModal: {
            title: 'Mehr Keywords können nicht hinzugefügt werden',
            content: 'Du hast das Limit von 200 Keywords erreicht. Lösche andere Keywords, bevor du neue hinzufügst.',
            confirm: 'OK'
        },
        importSetsModal: {
            title: 'Keyword Konfiguration Importieren ',
            subTitle: 'Beim Importieren der Konfiguration wird die aktuelle Konfiguration des Teams überschrieben.',
            previewSets: 'Wähle ein Team aus, um eine Vorschau der zu importierenden Sets anzuzeigen.',
            sets: 'Sets',
            whiteListSets: '„Sagen”-Gebrauch Sets',
            blackListSets: '„Nicht Sagen”-Gebrauch Sets',
            cancel: 'Abbrechen',
            confirm: 'Importieren',
            importedSuccess: 'Keyword-Konfiguration importiert'
        },
        emptySets: {
            title: 'Erzeugen Sie Anrufeinblicke und Agentenfeedback mit Keywords',
            subTitle: `Bevor i2x Ansprache-Feedback anzeigen kann, musst du Sets mit "„Sagen”-Gebrauch" & "„Nicht Sagen”-Gebrauch"-Wörtern erstellen`
        },
        conditionsModal: {
            discard: 'Abbrechen',
            heading: 'Tracke dieses Keyword in Äußerung...',
            utteranceTooltip: `Jedes Gespräch besteht aus kurzen Sprecheinheiten, die „Äußerungen” genannt werden. Eine Äußerung ist eine ununterbrochene Reihe gesprochener Wörter, welche aus lediglich einem Wort oder auch aus mehreren Sätzen bestehen kann.`,
            rowPlaceholder: 'Wörter hinzufügen...',
            rowHeadings: {
                with: '...nur mit:',
                without: '...nur ohne:',
                unless: '...außer es ist:'
            },
            unsupportedWordsError:
                'Wörter können teilweise nicht transkribiert werden und die Bedingung wird nicht angewendet.',
            conditionsMaxWordsError: 'Du kannst max. 25 Wörter pro Bedingung hinzufügen',
            unlessWordsError:
                'Die Außer-Bedingung muss %{targetWord} enhalten, damit die Phrase vom Tracking ausgeschlossen ist'
        },
        teamSelectorLabel: 'Teams',
        teamSelectorLabelPlaceholder: 'Team auswählen',
        updateKeywords: {
            [NOTIFICATION_TYPES.SUCCESS]: 'Keywords erfolgreich aktualisiert',
            [NOTIFICATION_TYPES.ERROR]: 'Aktualisierung der Keywords fehlgeschlagen',
            [NOTIFICATION_TYPES.IMPORTED]: 'Keyword-Konfiguration importiert',
            [NOTIFICATION_TYPES.ERROR_WHITELIST]: 'Keywords-Aktualisierung fehlgeschlagen: Whitelist nicht gültig',
            [NOTIFICATION_TYPES.ERROR_BLACKLIST]: 'Keywords-Aktualisierung fehlgeschlagen: Blacklist nicht gültig'
        },
        unsupportedTooltip:
            'Dieses Wort kann nicht korrekt transkribiert werden. Es wird nicht erfasst und ist für die Agenten nicht sichtbar.',
        unsupportedModal: {
            title: 'Manche Keywords können nicht genutzt werden',
            contentSingle: `Die i2x Spracherkennung kann derzeit dieses Wort nicht korrekt transkribieren. Es wird nicht zum Desktop Assistenten hinzugefügt oder im Dashboard getrackt.`,
            contentPlural: `Die i2x Spracherkennung kann derzeit diese %{count} Wörter nicht korrekt transkribieren.`,
            content1: `Sie werden nicht zum Desktop Assistenten hinzugefügt oder im Dashboard getrackt.`,
            content2: `Um die Sprache-zu-Text Transkription durch kundenspezifisches Vokabular (wie Fachterminologie oder Markennamen) zu verbessern, sende bitte eine Anfrage an: %{email}`,
            confirm: 'OK'
        },
        matchingLogicLimits: {
            warningFlexibleKeywordsLimitExceeded: {
                title: 'Dieses "Sagen"-Wort ist nicht für die flexible Erkennungslogik geeignet!',
                text: 'Die flexible Erkennungslogik ist nur für „Sagen“-Wörter mit maximal zwei verwandten Begriffen verfügbar. Stellen Sie sicher, dass dieses „Sagen“-Wort nur zwei verwandte Begriffe hat.',
                example: 'Beispiel',
                keyword1: 'Wie kann ich Ihnen helfen',
                keyword2: 'was kann ich heute für Sie tun'
            },
            warningFlexibleKeywordNotEligible: {
                title: 'Grenze für "Sagen"-Wörter mit flexibler Erkennungslogik überschritten!',
                text: 'Die flexible Erkennungslogik ist nur für maximal drei Keywords aus der "Sagen"-Spalte verfügbar.'
            },
            noMoreKeywordsCanBeAdded:
                'Wegen der flexiblen Erkennungslogik können keine weiteren "Sagen"-Wörter hinzugefügt werden.'
        },
        phrasesDetection: {
            callStartKeyword: 'Anruf Start Schlüsselwort',
            callStopKeyword: 'Anruf Stop Schlüsselwort',
            keywordsNotProvided: {
                title: 'Anrufereignis-Schlüsselwörter fehlen',
                text: 'Es wurden keine Schlüsselwörter angegeben. Bitte geben Sie die erforderlichen Schlüsselwörter ein, einschließlich des Anruf Start / Stop Schlüsselworts, um fortzufahren.'
            }
        }
    },
    userManager: {
        titleTeams: 'Team-Management',
        titleUsers: 'Benutzer-Management',
        createTeam: 'Team erstellen',
        listUsers: 'Benutzerliste',
        createUser: 'Benutzer erstellen',
        importUsers: 'Benutzer importieren',
        listTeams: 'Teamliste',
        teamNameLabel: 'Teamname',
        saveNew: 'Erstellen',
        update: 'Änderungen speichern',
        discard: 'Verwerfen',
        delete: 'Team löschen',
        createTeamTitle: 'Team erstellen',
        updateTeamTitle: 'Team ändern',
        createUserTitle: 'Benutzer erstellen',
        updateUserTitle: 'Benutzer ändern',
        locationUsersList: 'Teammitglieder hinzufügen',
        teamMembersList: 'Teammitglieder',
        emptyTeam: 'Keine Teammitglieder hinzugefügt',
        emptyLocationUsers: 'Alle Agenten sind bereits einem Team zugeordnet',
        saveNewTeamSuccess: 'Team erfolgreich erstellt',
        error: 'Vorgang fehlgeschlagen. Bitte versuchen Sie es später erneut.',
        searchUsers: 'Benutzer suchen',
        searchTeams: 'Team suchen',
        showDissociatedUsers: 'Deaktivierte Benutzer anzeigen',
        selectTeam: 'Team auswählen',
        deactivated: 'Deaktiviert',
        deactivatedTooltip: 'Dieser Benutzer ist deaktiviert und kann sich nicht anmelden',
        unknown: 'Unbekannt',
        saveNewTeamFail: {
            [errorCodes.REQUEST_ERROR]: 'Team konnte nicht erstellt werden',
            [errorCodes.userManager.TEAM_NAME_NOT_UNIQUE]:
                `Ein Team mit diesem Namen existiert bereits. Bitte wählen Sie einen anderen Namen für Ihr Team.`
        },
        updateTeamSuccess: 'Änderungen erfolgreich gespeichert',
        updateTeamFail: {
            [errorCodes.REQUEST_ERROR]: 'Änderungen konnten nicht gespeichert werden',
            [errorCodes.userManager.TEAM_NAME_NOT_UNIQUE]:
                `Ein Team mit diesem Namen existiert bereits. Bitte wählen Sie einen anderen Namen für Ihr Team.`
        },
        tooltips: {
            agents: `Ein Agent kann nur je einem Team angehören. Ein Team kann mehrere Teamleiter haben.`,
            editTeam: 'Teammitgliedern hinzufügen oder entfernen',
            editUser: 'Benutzer hinzufügen oder entfernen',
            team: 'Bitte füge mindestens einen Teamleiter zu diesem Team hinzu',
            teamNotValid: 'Aktualisierung fehlgeschlagen. Bitte wenden Sie sich an den Support.'
        },
        validation: {
            emptyTeamName: 'Bitte benennen Sie Ihr Team.',
            duplicatedTeamName: `Ein Team mit diesem Namen existiert bereits. Bitte wählen Sie einen anderen Namen für Ihr Team.`,
            teamNameTooLong: 'Team name should be no more than %{max} characters long'
        },
        saveEmptyDialog: {
            title: 'Keine Teammitglieder ',
            content: `Sie haben keine Teammitglieder hinzugefügt, Team trotzdem erstellen? `,
            confirm: 'Erstellen',
            cancel: 'Abbrechen'
        },
        discardNewDialog: {
            title: 'Team verwerfen?',
            content: 'Das Team wird nicht erstellt, Sie verwerfen die Eingabe.',
            confirm: 'Verwerfen',
            cancel: 'Abbrechen'
        },
        discardUpdateDialog: {
            title: 'Änderungen verwerfen?',
            content: `Ihre Änderungen werden nicht gespeichert.`,
            confirm: 'Verwerfen',
            cancel: 'Abbrechen'
        },
        deleteDialog: {
            title: 'Team löschen',
            content: `Dieses Team und alle zugehörigen Keywords werden dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.`,
            confirm: 'Löschen',
            cancel: 'Abbrechen',
            errorTeamNotEmpty: 'Bitte entfernen Sie zuerst alle Teammitglieder.',
            deleteTeamFail: 'Team konnte nicht gelöscht werden'
        },
        columns: {
            label: 'Team',
            managerNames: 'Teamleiter',
            agentsCount: 'Agenten',
            activeLastWeek: 'Aktiv in der letzten Woche',
            activeLastMonth: 'Aktiv in der letzten Monat',
            lastCallDate: 'Letzer Anruf',
            lastLoginDate: 'Letzer Login',
            neverLoggedIn: 'Nie angemeldet',
            role: 'Rolle',
            agent: 'Name',
            telephonyId: 'Telefonie-Login-ID',
            password: 'Passwort',
            email: 'E-Mail',
            id: 'ID',
            name: 'Name',
            companyUsername: 'Benutzername',
            language: 'Sprache',
            dateJoined: 'Beitrittsdatum',
            passwordChangedAt: 'Passwort geändert am',
            status: 'Status',
            team: 'Team',
            teams: 'Teams',
            firstName: 'Vorname',
            lastName: 'Nachname'
        },
        telephonyId: {
            tooltipHelperText: {
                paragraph1: 'Die Telefonie-Login-ID ist die ID, mit der sich der Agent am Telefonsystem anmeldet.',
                paragraph2:
                    'Jeder aktive Agent benötigt eine ID. Ohne eine Telefonie-Login-ID kann der i2x Assistant keine Anrufe empfangen.'
            },

            delete: {
                title: 'Telefonie-Login-ID entfernen?',
                body: 'Ohne eine Telefonie-Login-ID kann der i2x Assistant keine Anrufe erhalten.',
                confirmButtonText: 'Entfernen'
            },
            addId: 'ID hinzufügen',
            editId: 'ID bearbeiten',
            removeId: 'ID entfernen',
            addedMessage: 'Telefonie-Login-ID hinzugefügt',
            savedMessage: 'Änderungen der Telefonie-Login-ID gespeichert',
            removedMessage: 'Telefonie-Login-ID entfernt',
            errorMessage: 'Telefonie-Login-ID nicht gespeichert',
            errorInUseMessage: 'Telefonie-Login-ID wird bereits verwendet',
            validationMessage: 'Die Telefonie-Login-ID sollte nur aus Zahlen bestehen',
            duplicateIdMessage: 'Diese Telefonie-Login-ID ist bereits einem anderen Agenten zugewiesen',
            saveFailedMessage: 'Telefonie-Login-ID konnte nicht gespeichert werden'
        },
        password: {
            addId: 'Passwort festlegen',
            savedMessage: 'Passwort gespeichert',
            errorMessage: 'Passwort nicht gespeichert',
            validationMessageUnknownError: 'Es ist leider ein unbekannter Fehler aufgetreten.',
            validationMessagePasswordTooWeak: 'Das Passwort ist zu schwach.',
            tip: 'Das Passwort sollte Groß- und Kleinbuchstaben, Zahlen und Sonderzeichen enthalten. Vermeiden Sie gängige Wörter und Reihenfolgen. Für eine bessere Sicherheit, sollte es so lang und komplex wie möglich sein.',
            roleNotSupported:
                'Für diese Rolle verwenden Sie bitte das Passwort zurücksetzen-Formular auf der Anmelde-Seite.'
        },
        companyUsername: {
            validationMessage: 'Bitte geben Sie einen gültigen Benutzernamen ein.',
            duplicateIdMessage: 'Dieser Benutzername ist bereits einem anderen Benutzer zugewiesen',
            saveFailedMessage: 'Fehler beim Speichern des Benutzernamens',
            editId: 'Benutzername bearbeiten',
            addId: 'Benutzername hinzufügen'
        },
        validationMessages: {
            emailRequired: 'Bitte geben Sie Ihre E-Mail-Adresse ein.',
            emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
            emailExists: 'Diese E-Mail-Adresse wird bereits verwendet.',
            firstNameRequired: 'Bitte geben Sie Ihren Vornamen ein.',
            firstNameTooLong: 'Der Vorname darf maximal 30 Zeichen lang sein.',
            lastNameRequired: 'Bitte geben Sie Ihren Nachnamen ein.',
            lastNameTooLong: 'Der Nachname darf maximal 30 Zeichen lang sein.',
            roleRequired: 'Bitte wählen Sie eine Rolle aus.',
            companyUsernameRequired: 'Bitte geben Sie einen Benutzernamen ein.',
            companyUsernameInvalid: 'Bitte geben Sie einen gültigen Benutzernamen ein.',
            companyUsernameExists: 'Dieser Benutzername wird bereits verwendet.',
            companyUsernameTooLong: 'Der Benutzername darf maximal 60 Zeichen lang sein.',
            teamNotFound: 'Team nicht gefunden'
        },
        errors: {
            userNotFound: 'Benutzer nicht gefunden',
            failedToSaveUser: 'Benutzer konnte nicht gespeichert werden'
        },
        userCrudMessages: {
            updateUserSuccess: 'Benutzer erfolgreich aktualisiert',
            createUserSuccess: 'Benutzer erfolgreich erstellt',
            updateUserError: 'Fehler beim Aktualisieren des Benutzers',
            createUserError: 'Fehler beim Erstellen des Benutzers',
            deleteUserSuccess: 'Benutzer erfolgreich gelöscht',
            deleteUserError: 'Fehler beim Löschen des Benutzers'
        },
        status: {
            ACTIVE: 'Aktiv', // Active
            INCIDENTAL: 'Irrtümlich erstellt', // Incidental
            DISASSOCIATED: 'Abgekoppelt', // Disassociated
            TO_DISASSOCIATE: 'Löschung ausstehend' // To disassociate
        },
        defaultTeamMessage: 'Alle neuen Agenten werden automatisch zu dem Team hinzugefügt, das zuerst erstellt wurde',
        userListSpecialFilter: {
            title: 'Spezielle Filter',
            none: 'Keine',
            withoutLogin: 'Ohne Login',
            withoutTeam: 'Ohne Team',
            lastLoginMoreThan30DaysAgo: 'Letzter Login mehr als 30 Tage her',
            withoutCalls: 'Ohne Anrufe',
            lastCallMoreThan30DaysAgo: 'Letzter Anruf mehr als 30 Tage her'
        },
        import: {
            title: 'Benutzer importieren',
            columns: {
                status: 'Status'
            },
            fields: {
                email: 'E-Mail',
                companyUsername: 'Benutzername',
                firstName: 'Vorname',
                lastName: 'Nachname',
                team: 'Team',
                password: 'Passwort',
                skip: 'Spalte überspringen'
            },
            buttons: {
                saveAll: 'Alle speichern',
                savingAll: 'Wird gespeichert...',
                newImport: 'Neuer Import',
                parseData: 'Daten analysieren'
            },
            mappedReady: 'Bereit',
            mappedMissingRequired: 'Erforderliche Felder fehlen',
            pasteInstructions:
                'Kopiere Daten aus Excel oder Sheets und füge sie unten ein. Spalten werden automatisch erkannt.',
            pastePlaceholder: 'Tabellendaten hier einfügen...',
            alerts: {
                mapRequiredFields: 'Bitte weise E-Mail und Benutzername Spalten zu, um fortzufahren.'
            },
            tooltips: {
                rowHasErrors: 'Zeile enthält Fehler',
                saveRow: 'Diesen Benutzer speichern',
                removeRow: 'Diese Zeile entfernen'
            },
            confirmRemoveRow: 'Diese Zeile aus dem Import entfernen?',
            confirmReset: 'Alle importierten Daten löschen und einen neuen Import starten?',
            errors: {
                saveFailed: 'Speichern fehlgeschlagen',
                validationFailed: 'Validierung fehlgeschlagen'
            }
        }
    },
    pageNotFound: {
        title: 'Seite konnte nicht gefunden werden...',
        subTitle: 'Das wäre nie während eines Anrufs geschehen.',
        buttonText: 'Zur Hauptseite'
    },
    callHistoryMaintenance: {
        title: 'Anrufe sind vorübergehend nicht verfügbar',
        subTitle:
            'unsere i2x-Ingenieure arbeiten aktiv an einer Lösung. Bitte versuche es später erneut. Wir entschuldigen uns für die Unannehmlichkeiten.'
    },
    snackbar: {
        messages: {
            applied: 'Änderungen erfolgreich angewendet',
            saved: 'Änderungen erfolgreich gespeichert',
            requestFail: 'Beim Abrufen vom Server ist ein Fehler aufgetreten',
            urlCopySuccess: 'Link in die Zwischenablage kopiert'
        }
    },
    callHistory: {
        deletion: {
            button: 'Anrufdaten löschen',
            customer: 'Kundendaten löschen',
            call: 'Gesamten Anruf löschen',
            customerConfirm:
                'Kundentranskript, Stimmungsanalyse und Kundenaudio dieses Anrufs löschen? Dies kann nicht rückgängig gemacht werden.',
            callConfirm:
                'Diesen Anruf und alle zugehörigen Daten dauerhaft löschen? Dies kann nicht rückgängig gemacht werden.',
            cancel: 'Abbrechen',
            confirm: 'Löschen',
            error: 'Anrufdaten konnten nicht gelöscht werden. Bitte erneut versuchen.'
        },
        defaultTeamMessage:
            'Aufzeichnungen von Anrufen die zwischen 15.01 und 17.01.2021 geführt wurden sind aktuell nicht verfügbar. Wir arbeiten intensiv daran, dass die Anrufe sobald wie möglich wieder erscheinen.',
        general: {
            today: 'Heute',
            yesterday: 'Gestern',
            filters: 'Filter',
            beta: 'BETA',
            scorecardRating: 'Scorecard Rating'
        },
        navigation: {
            previous: 'Vorheriger Anruf',
            next: 'Nächster Anruf'
        },
        filterButtons: {
            clear: 'Löschen',
            apply: 'Anwenden'
        },
        searchSpeakerOptions: {
            agents: 'Agenten',
            customers: 'Kunden',
            any: 'Jemand'
        },
        tableHeaders: {
            callId: 'Anruf-ID',
            callType: 'Typ',
            botProject: 'Projekt',
            caller: 'Anrufer',
            agentName: 'Agent',
            teamName: 'Team',
            callTopics: 'Anrufthemen',
            doSay: '„Sagen”-Gebrauch',
            duration: 'Gesprächsdauer',
            callStart: 'Startzeit',
            scorecard: 'Scorecard Rating',
            points: 'Punkte',
            customerSentiment: 'Stimmung (Kunde)',
            agentSentiment: 'Stimmung (Agent)',
            sentimentHelpCustomer:
                'Stimmung spiegelt den emotionalen Ton Ihres Kunden wider. Diese Funktion analysiert das Verhältnis von negativen, positiven und neutralen Stimmmungskategorien, die vom Kunden während des Anrufs geäußert wurden. Es kann bis zu einer Stunde nach Beendigung des Anrufs dauern, bis die Analyse verfügbar ist.',
            sentimentHelpAgent:
                'Stimmung spiegelt den emotionalen Ton Ihres Agenten wider. Diese Funktion analysiert das Verhältnis von negativen, positiven und neutralen Stimmmungskategorien, die vom Agenten während des Anrufs geäußert wurden. Es kann bis zu einer Stunde nach Beendigung des Anrufs dauern, bis die Analyse verfügbar ist.',
            goToCall: 'Zur Call Seite',
            sorting: {
                positiveAscending: 'Positiv (aufsteigend)',
                positiveDescending: 'Positiv (absteigend)',
                neutralAscending: 'Neutral (aufsteigend)',
                neutralDescending: 'Neutral (absteigend)',
                negativeAscending: 'Negativ (aufsteigend)',
                negativeDescending: 'Negativ (absteigend)'
            }
        },
        download: {
            download: 'Herunterladen',
            transcript: 'Transkript ',
            audio: 'Audio'
        },
        view: {
            title: 'Anrufdetails',
            close: 'Schließen',
            createdBy: 'Erstellt von',
            messageByUser: 'Kunde',
            messageByBot: 'Bot',
            messageByAgent: 'Agent',
            duration: 'Gesprächsdauer',
            emptyTranscript: 'Es gibt keine Abschrift des Anrufs'
        },
        filters: {
            [filterIds.callHistoryViewFilter]: {
                label: 'Ansicht'
            },
            [filterIds.callHistoryTimeRangeFilter]: {
                label: 'Gesprächsdauer',
                clearButton: 'Löschen',
                maxValue: 'Beliebig'
            },
            [filterIds.callHistoryDoSayScoreFilter]: {
                label: '„Sagen”-Gebrauch'
            },
            dateRange: 'Datumsbereich',
            callType: 'Typ',
            caller: 'Anrufer',
            duration: 'Gesprächsdauer',
            projectName: 'Projekt'
        },
        callTopics: {
            label: 'Anrufthemen',
            empty: 'Kein Thema erkannt',
            emptyTooltip: 'Es wurde kein existierendes Thema erkannt.',
            unavailable: ''
        },
        emptyState: {
            title: 'Wir konnten keine passenden Anrufe finden',
            text: 'Versuche deine Suchkriterien zu ändern'
        },
        sentiment: {
            positive: 'Positiv',
            neutral: 'Neutral',
            negative: 'Negativ',
            status: {
                processing: 'in Verarbeitung',
                notAvailable: '-'
            }
        },
        callDetails: {
            callTopicsSection: 'Anrufthemen',
            doSaySection: '„Sagen”-Gebrauch',
            dontSaySection: '„Nicht Sagen”-Gebrauch',
            transcript: 'Transkript',
            searchPlaceholder: 'Diesen Anruf durchsuchen...',
            customer: 'Kunde',
            agent: 'Agent',
            emptyState: {
                title: 'Es gibt (noch) kein Transkript für diesen Anruf',
                text: 'Bitte beachte, dass das Transkript in der Regel etwa eine Minute nach Beendigung des Anrufs zur Verfügung steht. Falls bereits mehr Zeit vergangen ist, bist du entweder über einen Fehler gestolpert oder wir konnten keine gesprochenen Wörter aufnehmen.'
            },
            player: {
                playbackSpeed: 'Geschwindigkeit',
                normal: '1x',
                fast: '1,5x',
                faster: '2x'
            },
            sentimentAnalysis: {
                title: {
                    customer: 'Stimmung des Kunden',
                    agent: 'Stimmung des Mitarbeiters'
                },
                tooltip: {
                    customer:
                        'Diese Berechnung basiert lediglich auf der Gesamtanzahl der Äußerungen auf der Kundenseite.',
                    agent: 'Diese Berechnung basiert lediglich auf der Gesamtanzahl der Äußerungen auf der Agentenseite.'
                },
                positive: 'Positiv',
                neutral: 'Neutral',
                negative: 'Negativ'
            },
            feedback: {
                title: 'Bewerte die Stimmung in diesem Anruf',
                subtitle:
                    'Dieser Anruf wurde als positiv bewertet. Wenn Sie mit dieser Bewertung nicht übereinstimmen, wählen Sie bitte Ihre persönliche Bewertung aus. Bevor Sie eine Bewertung abgeben, empfehlen wir Ihnen, die gesamte Aufzeichnung anzuhören.',
                choiceLabel: 'Bitte wählen Sie die Bewertung aus, die Sie für richtig halten:',
                positive: 'Positiv',
                neutral: 'Neutral',
                negative: 'Negativ',
                additionalInformation: 'Haben Sie noch zusätzliche Kommentare oder Feedback zu teilen? (Optional)',
                submit: 'Absenden',
                success: 'Vielen Dank für Ihre Rückmeldung!',
                report: 'Stimmungsbewertung melden'
            },
            callSummary: {
                copySuccess: 'Kopiert!',
                header: 'Zusammenfassung',
                generateMessage:
                    'Sie können jetzt eine Zusammenfassung für diesen Anruf erstellen lassen. Die Qualität der Zusammenfassung kann beeinträchtigt werden, wenn die Kundenseite nicht aufgezeichnet wurde.',
                generateButton: 'Generieren',
                generatingMessage: 'Wird erstellt...',
                errorMessage: 'Die Zusammenfassung konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
                feedback: 'Ist diese Zusammenfassung hilfreich?',
                feedbackGiven: 'Danke für Ihr Feedback!',
                copy: 'Kopieren'
            }
        },
        evaluation: {
            title: 'Anrufbewertung',
            create: 'Anrufbewertung erstellen',
            sidebarHeader: 'Anrufauswertungen',
            backToList: 'Zurück zu Auswertungen'
        },
        anonymization: {
            [ANONYMIZATION_TYPES.NUMBER]: 'Nummer',
            [ANONYMIZATION_TYPES.ADDRESS]: 'Adresse',
            [ANONYMIZATION_TYPES.NAME]: 'Name',
            noType: 'anonymisiert'
        },
        transcriptSearch: {
            searchResultHeader: 'Es werden %{resultCount} von %{total} Ergebnissen in diesem Anruf angezeigt',
            searchResultHeaderOneResult: 'Es wird %{resultCount} Ergebnis in diesem Anruf angezeigt',
            searchResultHeaderTwoResults: 'Es werden %{resultCount} Ergebnisse in diesem Anruf angezeigt',
            resultsCount: '%{totalCount} Anrufe',
            viewCall: 'Anruf ansehen',
            saidBy: 'Gesagt von: ',
            searchPlaceholder: 'Suche in Anrufen...'
        },
        callHistoryScoreCardFilter:
            'Die Scorecard wird über Nacht ausgewertet. Verwende den Filter, um Anrufe zu finden, die bereits bewertet wurden.',
        preset: {
            new: 'Neu erstellen',
            changeFiltersToSave: 'Für eine neue Voreinstellung bitte Filter ändern',
            title: 'Voreinstellung',
            static: 'Statisch',
            dynamic: 'Dynamisch',
            staticNames: {
                'shorter-than-2-minutes': 'Unter 2 Min.',
                'shorter-than-2-7-minutes': '2–7 Min.',
                'longer-than-14-28-minutes': '14–28 Min.',
                'longer-than-28-minutes': 'Über 28 Min.'
            },
            removeModal: {
                title: 'Voreinstellung entfernen',
                content: 'Möchten Sie diese Voreinstellung wirklich entfernen?',
                cancel: 'Abbrechen',
                confirm: 'Bestätigen'
            },
            saveModal: {
                title: 'Neue Voreinstellung erstellen',
                inputLabel: 'Voreinstellungsname',
                cancel: 'Abbrechen',
                confirm: 'Speichern'
            }
        }
    },
    date: {
        short: {
            seconds: ' s',
            minutes: ' min',
            hours: ' h'
        }
    },
    number: {
        percentage: '%{value} %'
    }
};
